import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ISchedulerData,
  ISchedulerDependency,
  ISchedulerTask,
  LinkType,
} from '@interfaces/IScheduler';
import { depSummary } from '../../lib/depSummary';
import styles from './TaskPanel.module.css';

// D-ORCA owns the schedule now, so the full FS/SS/FF/SF link model with lag is
// editable — no longer restricted to before→after (FS/0) by the Genba transport.

const LINK_TYPES: LinkType[] = ['FS', 'SS', 'FF', 'SF'];

// ---------------------------------------------------------------------------
// useAddDepForm — encapsulates the four pieces of local state for an add-dep
// inline form and their reset logic. Used once for predecessors and once for
// successors so the state is not interleaved and the reset is self-contained.
// ---------------------------------------------------------------------------

interface IAddDepFormState {
  open: boolean;
  taskId: string;
  type: LinkType;
  lagDays: number;
  setOpen: (v: boolean) => void;
  setTaskId: (v: string) => void;
  setType: (v: LinkType) => void;
  setLagDays: (v: number) => void;
  reset: () => void;
}

function useAddDepForm(): IAddDepFormState {
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState('');
  const [type, setType] = useState<LinkType>('FS');
  const [lagDays, setLagDays] = useState(0);

  const reset = () => {
    setTaskId('');
    setType('FS');
    setLagDays(0);
    setOpen(false);
  };

  return { open, taskId, type, lagDays, setOpen, setTaskId, setType, setLagDays, reset };
}

// ---------------------------------------------------------------------------
// DependencyRow — displays one existing dependency row (editable type + lag)
// ---------------------------------------------------------------------------

interface IDependencyRowProps {
  dependency: ISchedulerDependency;
  otherTaskName: string;
  onUpdate?: (dependency: ISchedulerDependency) => void;
  onDelete?: (dependencyId: string) => void;
}

function DependencyRow({ dependency, otherTaskName, onUpdate, onDelete }: IDependencyRowProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.depRow}>
      <span className={styles.depTaskName} title={otherTaskName}>
        {otherTaskName}
      </span>
      <select
        className={styles.depSelect}
        value={dependency.type}
        disabled={!onUpdate}
        onChange={(e) => onUpdate?.({ ...dependency, type: e.target.value as LinkType })}
        aria-label={t('scheduler.panel.depsTypeLabel')}
      >
        {LINK_TYPES.map((type) => (
          <option key={type} value={type}>{t(`scheduler.linkType.${type}`)}</option>
        ))}
      </select>
      <input
        type="number"
        className={styles.depLag}
        value={dependency.lagDays}
        disabled={!onUpdate}
        onChange={(e) => onUpdate?.({ ...dependency, lagDays: Number(e.target.value) })}
        aria-label={t('scheduler.panel.depsLagLabel')}
      />
      <button
        type="button"
        className={styles.depDelBtn}
        onClick={() => onDelete?.(dependency.id)}
        disabled={!onDelete}
        aria-label={t('scheduler.arrows.deleteDependency')}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddDependencyRow — inline add-dependency form
// ---------------------------------------------------------------------------

interface IAddDependencyRowProps {
  direction: 'predecessor' | 'successor';
  eligibleTasks: ISchedulerTask[];
  taskId: string;
  type: LinkType;
  lagDays: number;
  onTaskId: (id: string) => void;
  onType: (type: LinkType) => void;
  onLagDays: (lagDays: number) => void;
  onAdd: () => void;
  onCancel: () => void;
  canAdd: boolean;
}

function AddDependencyRow({
  direction, eligibleTasks, taskId, type, lagDays,
  onTaskId, onType, onLagDays, onAdd, onCancel, canAdd,
}: IAddDependencyRowProps) {
  const { t } = useTranslation();
  const addLabel = direction === 'predecessor'
    ? t('scheduler.panel.depsAddPredecessor')
    : t('scheduler.panel.depsAddSuccessor');
  return (
    <div className={styles.depAddRow}>
      <select
        className={styles.depSelect}
        value={taskId}
        onChange={(e) => onTaskId(e.target.value)}
        aria-label={t('scheduler.panel.depsPickTask')}
        autoFocus
      >
        <option value="">{t('scheduler.panel.depsPickTask')}</option>
        {eligibleTasks.map((task) => (
          <option key={task.id} value={task.id}>{task.name || task.id}</option>
        ))}
      </select>
      <select
        className={styles.depSelect}
        value={type}
        onChange={(e) => onType(e.target.value as LinkType)}
        aria-label={t('scheduler.panel.depsTypeLabel')}
      >
        {LINK_TYPES.map((linkType) => (
          <option key={linkType} value={linkType}>{t(`scheduler.linkType.${linkType}`)}</option>
        ))}
      </select>
      <input
        type="number"
        className={styles.depLag}
        value={lagDays}
        onChange={(e) => onLagDays(Number(e.target.value))}
        aria-label={t('scheduler.panel.depsLagLabel')}
      />
      <button
        type="button"
        className={styles.depAddBtn}
        onClick={onAdd}
        disabled={!canAdd}
      >
        {addLabel}
      </button>
      <button
        type="button"
        className={styles.depAddCancel}
        onClick={onCancel}
        aria-label={t('scheduler.panel.cancel')}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DepLinkBlock — renders one direction's list of existing links + add form.
// Extracts the repeated predecessor/successor JSX block to a single reusable
// sub-component so DependenciesSection only orchestrates the two instances.
// ---------------------------------------------------------------------------

interface IDepLinkBlockProps {
  direction: 'predecessor' | 'successor';
  links: ISchedulerDependency[];
  eligibleTasks: ISchedulerTask[];
  taskNameById: Map<string, string>;
  form: IAddDepFormState;
  onUpdate?: (dependency: ISchedulerDependency) => void;
  onDelete?: (dependencyId: string) => void;
  onAdd?: (dependency: ISchedulerDependency) => void;
  taskId: string;
}

function DepLinkBlock({
  direction, links, eligibleTasks, taskNameById, form, onUpdate, onDelete, onAdd, taskId,
}: IDepLinkBlockProps) {
  const { t } = useTranslation();
  const subtitleKey = direction === 'predecessor'
    ? 'scheduler.panel.depsPredecessors'
    : 'scheduler.panel.depsSuccessors';
  const addLabelKey = direction === 'predecessor'
    ? 'scheduler.panel.depsAddPredecessor'
    : 'scheduler.panel.depsAddSuccessor';

  const handleAdd = () => {
    if (!form.taskId || !onAdd) return;
    onAdd({
      id: `dep-${crypto.randomUUID()}`,
      predecessorId: direction === 'predecessor' ? form.taskId : taskId,
      successorId: direction === 'predecessor' ? taskId : form.taskId,
      type: form.type,
      lagDays: form.lagDays,
    });
    form.reset();
  };

  return (
    <>
      <p className={styles.depsSubtitle}>{t(subtitleKey)}</p>
      {links.length === 0 && (
        <p className={styles.depEmpty}>{t('scheduler.panel.depsNoLinks')}</p>
      )}
      {links.map((dependency) => (
        <DependencyRow
          key={dependency.id}
          dependency={dependency}
          otherTaskName={
            direction === 'predecessor'
              ? (taskNameById.get(dependency.predecessorId) ?? dependency.predecessorId)
              : (taskNameById.get(dependency.successorId) ?? dependency.successorId)
          }
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      ))}
      {!form.open ? (
        <button
          type="button"
          className={styles.depAddToggle}
          onClick={() => form.setOpen(true)}
          disabled={!onAdd}
        >
          <span className={styles.depAddToggleIcon} aria-hidden>+</span>
          {t(addLabelKey)}
        </button>
      ) : (
        <AddDependencyRow
          direction={direction}
          eligibleTasks={eligibleTasks}
          taskId={form.taskId}
          type={form.type}
          lagDays={form.lagDays}
          onTaskId={form.setTaskId}
          onType={form.setType}
          onLagDays={form.setLagDays}
          onAdd={handleAdd}
          onCancel={form.reset}
          canAdd={!!form.taskId && !!onAdd}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// DependenciesSection — colocated sub-component (only used in this drawer)
// ---------------------------------------------------------------------------

interface IDependenciesSectionProps {
  task: ISchedulerTask;
  mode: 'create' | 'edit';
  data: ISchedulerData;
  readOnly?: boolean;
  onAdd?: (dependency: ISchedulerDependency) => void;
  onUpdate?: (dependency: ISchedulerDependency) => void;
  onDelete?: (dependencyId: string) => void;
}

export function DependenciesSection({ task, mode, data, readOnly, onAdd, onUpdate, onDelete }: IDependenciesSectionProps) {
  const { t } = useTranslation();

  const predForm = useAddDepForm();
  const succForm = useAddDepForm();

  const isNewTask = mode === 'create';

  const predecessorLinks = data.dependencies.filter((dependency) => dependency.successorId === task.id);
  const successorLinks   = data.dependencies.filter((dependency) => dependency.predecessorId === task.id);

  const linkedPredIds = new Set(predecessorLinks.map((dependency) => dependency.predecessorId));
  const linkedSuccIds = new Set(successorLinks.map((dependency) => dependency.successorId));

  // Tasks on this task's own branch (ancestors + descendants) can't be linked:
  // a summary's span already contains its descendants', so such a link is a
  // logical loop (MS Project blocks it, and the store rejects it). Exclude them
  // from the pickers so only valid targets show.
  const sameBranchIds = useMemo(() => {
    const byId = new Map(data.tasks.map((entry) => [entry.id, entry]));
    const childrenByParent = new Map<string, string[]>();
    for (const entry of data.tasks) {
      if (entry.parentId == null) continue;
      if (!childrenByParent.has(entry.parentId)) childrenByParent.set(entry.parentId, []);
      childrenByParent.get(entry.parentId)!.push(entry.id);
    }
    const related = new Set<string>();
    // Ancestors.
    let current = byId.get(task.id);
    const seen = new Set<string>();
    while (current?.parentId != null && !seen.has(current.id)) {
      seen.add(current.id);
      related.add(current.parentId);
      current = byId.get(current.parentId);
    }
    // Descendants (BFS).
    const queue = [...(childrenByParent.get(task.id) ?? [])];
    while (queue.length) {
      const id = queue.shift()!;
      if (related.has(id)) continue;
      related.add(id);
      for (const child of childrenByParent.get(id) ?? []) queue.push(child);
    }
    return related;
  }, [data.tasks, task.id]);

  // Tasks eligible to be added as predecessor (not self, not on this branch, not already predecessor)
  const eligiblePreds = data.tasks.filter(
    (candidate) => candidate.id !== task.id && !sameBranchIds.has(candidate.id) && !linkedPredIds.has(candidate.id),
  );
  // Tasks eligible to be added as successor (not self, not on this branch, not already successor)
  const eligibleSuccs = data.tasks.filter(
    (candidate) => candidate.id !== task.id && !sameBranchIds.has(candidate.id) && !linkedSuccIds.has(candidate.id),
  );

  const taskNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const entry of data.tasks) m.set(entry.id, entry.name || entry.id);
    return m;
  }, [data.tasks]);

  // Read-only view — plain chip list, no interactive rows or add toggles.
  // Guard is placed after all hooks/derivations to satisfy Rules of Hooks.
  if (readOnly) {
    return (
      <section className={styles.depsSection}>
        <h3 className={styles.depsTitle}>{t('scheduler.panel.depsTitle')}</h3>

        <p className={styles.depsSubtitle}>{t('scheduler.panel.depsPredecessors')}</p>
        {predecessorLinks.length === 0
          ? <p className={styles.depEmpty}>{t('scheduler.panel.depsNoLinks')}</p>
          : (
            <ul className={styles.readonlyDepsList}>
              {predecessorLinks.map(dep => (
                <li key={dep.id} className={styles.readonlyDepChip}>
                  {taskNameById.get(dep.predecessorId) ?? dep.predecessorId}
                  <span className={styles.readonlyDepMeta}>· {depSummary(dep, t)}</span>
                </li>
              ))}
            </ul>
          )}

        <p className={styles.depsSubtitle}>{t('scheduler.panel.depsSuccessors')}</p>
        {successorLinks.length === 0
          ? <p className={styles.depEmpty}>{t('scheduler.panel.depsNoLinks')}</p>
          : (
            <ul className={styles.readonlyDepsList}>
              {successorLinks.map(dep => (
                <li key={dep.id} className={styles.readonlyDepChip}>
                  {taskNameById.get(dep.successorId) ?? dep.successorId}
                  <span className={styles.readonlyDepMeta}>· {depSummary(dep, t)}</span>
                </li>
              ))}
            </ul>
          )}
      </section>
    );
  }

  return (
    <section className={styles.depsSection}>
      <h3 className={styles.depsTitle}>{t('scheduler.panel.depsTitle')}</h3>

      {isNewTask ? (
        <p className={styles.depEmpty}>{t('scheduler.panel.depsSaveFirst')}</p>
      ) : (
        <>
          <DepLinkBlock
            direction="predecessor"
            links={predecessorLinks}
            eligibleTasks={eligiblePreds}
            taskNameById={taskNameById}
            form={predForm}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onAdd={onAdd}
            taskId={task.id}
          />
          <DepLinkBlock
            direction="successor"
            links={successorLinks}
            eligibleTasks={eligibleSuccs}
            taskNameById={taskNameById}
            form={succForm}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onAdd={onAdd}
            taskId={task.id}
          />
        </>
      )}
    </section>
  );
}
